import * as XLSX from "xlsx-js-style";

const CURRENCY_FORMAT = "$#,##0.00";
const NET_INCOME_FILL = { patternType: "solid", fgColor: { rgb: "E2EFDA" } };
const SAFETY_NET_FILL = { patternType: "solid", fgColor: { rgb: "FFFF00" } };
const BOLD_FONT = { bold: true };

const SAFETY_NET_ROWS = [
  "Mortgage Interest",
  "Depreciation expense",
  "Auto/Travel",
];
const SAFETY_NET_NOTE = "MANUAL INPUT REQUIRED - PROVIDE TO CPA";

/**
 * Generate and download a styled Schedule E Tax Packet Excel file.
 * @param {Object} pivot - { rows: Array<{category, amounts, total, isSubtotal?}>, properties: string[], entityByProperty?: { [propertyKey]: string } }
 * @param {string} yearLabel - e.g. "2025" or "Last12Months"
 * @param {{ singleColumn?: boolean }} [options] - If singleColumn: true, output one Amount column (no per-property columns). Use for tax prep export.
 */
export function downloadScheduleETaxPacketExcel(
  pivot,
  yearLabel,
  options = {}
) {
  const { rows, properties, entityByProperty } = pivot;
  const singleColumn = options.singleColumn === true;
  if (!rows?.length) {
    return false;
  }
  if (!singleColumn && !properties?.length) {
    return false;
  }

  // Insert safety net rows (CPA forcing function) before Total Income.
  // Skip "Mortgage Interest" if the user already entered it in expenses.
  const hasMortgageInterest = rows.some(
    (r) =>
      r.category === "Mortgage Interest" &&
      (r.total > 0 || Object.values(r.amounts || {}).some((v) => Number(v) > 0))
  );
  const safetyNetCats = SAFETY_NET_ROWS.filter(
    (cat) => cat !== "Mortgage Interest" || !hasMortgageInterest
  );
  const totalIncomeIdx = rows.findIndex((r) => r.category === "Total Income");
  const safetyNetRows = safetyNetCats.map((cat) => ({
    category: cat,
    amounts: singleColumn
      ? {}
      : Object.fromEntries((properties || []).map((p) => [p, 0])),
    total: 0,
    notes: SAFETY_NET_NOTE,
    isSafetyNet: true,
  }));
  const expandedRows =
    totalIncomeIdx >= 0
      ? [
          ...rows.slice(0, totalIncomeIdx),
          ...safetyNetRows,
          ...rows.slice(totalIncomeIdx),
        ]
      : [...rows, ...safetyNetRows];

  // Filter out blank rows (empty category)
  const filteredRows = expandedRows.filter(
    (row) => row.category && row.category.trim() !== ""
  );

  let data;
  if (singleColumn) {
    // One column: Category | Notes | Amount (no per-property columns)
    data = [["Category", "Notes", "Amount"]];
    filteredRows.forEach((row) => {
      const numVal =
        row.isSubtotal && (row.total === undefined || row.total === null)
          ? ""
          : typeof row.total === "number"
          ? row.total
          : "";
      data.push([row.category, row.notes || "", numVal]);
    });
  } else {
    const propertyHeaders = properties.map((_, i) => `Property ${i + 1}`);
    data = [["Category", "Notes", ...propertyHeaders, "Grand Total"]];
    // If we have entity/LLC per property, add a "Deposit account" row right after the header
    if (entityByProperty && Object.keys(entityByProperty).length > 0) {
      const entityRow = [
        "Deposit account",
        "",
        ...properties.map((prop) => entityByProperty[prop] ?? ""),
        "",
      ];
      data.push(entityRow);
    }
    filteredRows.forEach((row) => {
      const cells = [row.category, row.notes || ""];
      properties.forEach((prop) => {
        const val = row.amounts[prop];
        const numVal =
          row.isSubtotal && (val === undefined || val === null) ? "" : val ?? 0;
        cells.push(numVal);
      });
      cells.push(typeof row.total === "number" ? row.total : "");
      data.push(cells);
    });
  }

  const ws = XLSX.utils.aoa_to_sheet(data);

  const hasEntityRow =
    !singleColumn &&
    entityByProperty &&
    Object.keys(entityByProperty).length > 0;
  const dataRowOffset = hasEntityRow ? 2 : 1; // header + optional entity row

  // Apply styles to each cell
  const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
  for (let R = 0; R <= range.e.r; R++) {
    for (let C = 0; C <= range.e.c; C++) {
      const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[cellRef];
      if (!cell) continue;

      const row = R >= dataRowOffset ? filteredRows[R - dataRowOffset] : null;
      const isHeaderRow = R === 0;
      const isEntityRow = hasEntityRow && R === 1;
      const isNetIncomeRow = row?.category === "Net Income (Loss)";
      const isSafetyNetRow = row?.isSafetyNet;
      const isNumberCell =
        (singleColumn ? C === 2 : C > 1) &&
        (cell.t === "n" || (typeof cell.v === "number" && !isNaN(cell.v)));

      if (isHeaderRow) {
        cell.s = { font: BOLD_FONT };
      } else if (isEntityRow) {
        cell.s = { font: BOLD_FONT };
      } else if (isSafetyNetRow) {
        cell.s = {
          fill: SAFETY_NET_FILL,
          numFmt: isNumberCell ? CURRENCY_FORMAT : undefined,
        };
      } else if (isNetIncomeRow) {
        cell.s = {
          font: BOLD_FONT,
          fill: NET_INCOME_FILL,
          numFmt: isNumberCell ? CURRENCY_FORMAT : undefined,
        };
      } else if (isNumberCell) {
        cell.s = { numFmt: CURRENCY_FORMAT };
      }
    }
  }

  // Auto-adjust column widths
  const categoryWidth = Math.max(
    22,
    ...filteredRows.map((r) => String(r.category || "").length)
  );
  const notesWidth = Math.max(35, SAFETY_NET_NOTE.length + 2);
  const colWidths = [{ wch: categoryWidth }, { wch: notesWidth }];
  if (singleColumn) {
    colWidths.push({ wch: 14 });
  } else {
    (properties || []).forEach((_, i) => {
      colWidths.push({ wch: Math.max(12, `Property ${i + 1}`.length + 2) });
    });
    colWidths.push({ wch: 14 });
  }
  ws["!cols"] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Schedule E Summary");

  const filename = `Schedule-E-Tax-Packet-${yearLabel}.xlsx`;
  XLSX.writeFile(wb, filename);
  return true;
}
